import React from 'react';
import { FaGlobe, FaEnvelope, FaPhone, FaLinkedin, FaGithub, FaMapMarkerAlt } from 'react-icons/fa';
import './Resume.css';
import transition from '../../transition';
import SignatureButton from '../../components/SignatureButton/SignatureButton';
import resumeDownload from '../../assets/files/Kevin_Fuller_Resume_2026.pdf';

const Resume = () => {
  const resumeFilename = 'Kevin_Fuller_Resume_2026.pdf';

  const handlePrint = () => {
    const printWindow = window.open(resumeDownload, '_blank', 'noopener,noreferrer');
    if (!printWindow) return;

    const triggerPrint = () => {
      printWindow.focus();
      printWindow.print();
    };

    printWindow.addEventListener('load', triggerPrint, { once: true });
  };

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = resumeDownload;
    link.download = resumeFilename;
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <main className="resumePage">
      <div className="resumeButtonsWrapper">
        <SignatureButton to="/" variant="ghost" withArrow={false} magnetic={false}>
          Go back
        </SignatureButton>

        <div className="print-buttons-wrapper">
          <SignatureButton
            variant="ghost"
            withArrow={false}
            magnetic={false}
            onClick={handlePrint}
          >
            Print
          </SignatureButton>
          <SignatureButton variant="primary" magnetic={false} onClick={handleDownload}>
            Download
          </SignatureButton>
        </div>
      </div>

      <div className="resumeWrapper">
        <div className="resume">
          <section className="mt-none">
            <h1>Kevin Fuller</h1>
            <div className="resumeLinksWrapper">
              <ul>
                <li><FaGlobe /> <a href="https://kevinfuller.me">kevinfuller.me</a></li>
                <li><FaEnvelope /> <a href="mailto:kgfuller@yahoo.com">kgfuller@yahoo.com</a></li>
                <li><FaPhone /> <a href="tel:+18123811593">(812) 381-1593</a></li>
                <li><FaLinkedin /> <a href="https://www.linkedin.com/in/kevin-fuller1/">linkedin.com/in/kevin-fuller1/</a></li>
                <li><FaGithub /> <a href="https://github.com/Kevin-Fuller">github.com/Kevin-Fuller</a></li>
                <li><FaMapMarkerAlt /> Bloomington, IN, United States</li>
              </ul>
            </div>
          </section>

          <section className="resumeEducation">
            <h2>Professional Summary</h2>
            <div className="educationWrapper">
              <p>
                Senior Software Engineer with 8+ years building high-scale, data-driven web apps.
                Specializes in React, TypeScript, and Node.js — with a Human-Computer Interaction
                background that bridges engineering and product thinking. Track record of
                modernizing legacy systems and shipping features that measurably improve usability.
              </p>
            </div>
          </section>

          <section>
            <h2>Technical Skills</h2>
            <div className="technicalSkillsGrid">
              <h3>Languages:</h3>
              <p>JavaScript (ES6+), TypeScript, SQL, Python, PHP</p>
              <h3>Frontend:</h3>
              <p>React, Next.js, Tailwind CSS, Figma</p>
              <h3>Backend:</h3>
              <p>Node.js, Express</p>
              <h3>Database:</h3>
              <p>MySQL, Postgres, Firebase</p>
              <h3>Other:</h3>
              <p>REST APIs, System Design, End-to-End Testing, Git, Postman</p>
            </div>
          </section>

          <section>
            <h2>Work Experience</h2>
            <ul className="workExperienceItem">
              <li className="resumeWork">
                <div className="resumeWorkWrapper">
                  <h3>DealMachine</h3>
                  <em>Senior Software Engineer</em>
                  <p className="ml-auto">Remote</p>
                  <p>Jun. 2025 - Present</p>
                </div>
                <ul className="workExperienceInfo">
                  <li>Own and ship user-facing features across search, mapping, filtering, and bulk workflows for a platform processing billions of real estate records.</li>
                  <li>Drive frontend architecture decisions and establish UI patterns adopted team-wide, serving as the primary code reviewer for UI-heavy features.</li>
                  <li>Optimize search and location-based queries across billions of records, improving latency for time-sensitive real estate workflows.</li>
                  <li>Built a Playwright-based testing framework that eliminated hundreds of hours of manual QA per sprint.</li>
                </ul>
              </li>
              <li>
                <div className="resumeWorkWrapper">
                  <h3>Indiana Geological and Water Survey</h3>
                  <em>Senior Fullstack Web Developer</em>
                  <p className="ml-auto">Bloomington, IN</p>
                  <p>Jul. 2022 - 2025</p>
                </div>
                <ul className="workExperienceInfo">
                  <li>Led full migration of legacy ColdFusion system to React/Node.js as the sole decision-maker on stack selection — reducing page load times from 6s to near-instant and eliminating a years-long maintenance bottleneck.</li>
                  <li>Designed and built a custom CMS serving ~40 researchers and content editors, supporting structured data and publishing workflows.</li>
                  <li>Developed REST APIs and refactored MySQL schemas to improve data reliability and query performance.</li>
                  <li>Built data pipelines processing and surfacing large volumes of scientific datasets for public-facing tools.</li>
                </ul>
              </li>
              <li>
                <div className="resumeWorkWrapper">
                  <h3>BuffaLouie&apos;s</h3>
                  <em>Software Engineer (Head of IT)</em>
                  <p className="ml-auto">Bloomington, IN</p>
                  <p>Sep. 2018 – Jul. 2022</p>
                </div>
                <ul className="workExperienceInfo">
                  <li>Owned all technical systems — web, mobile, and internal tools — as sole engineer across 4 locations.</li>
                  <li>Developed a digital signage platform (PHP, MySQL, JavaScript) used across multiple business locations.</li>
                  <li>Co-built customer-facing mobile app that drove a 270% increase in online orders.</li>
                </ul>
              </li>
              <li>
                <div className="resumeWorkWrapper">
                  <h3>Freelance Web Developer</h3>
                  <em>Fullstack Web Developer</em>
                  <p className="ml-auto">Bloomington, IN</p>
                  <p>Jan. 2018 - Present</p>
                </div>
                <ul className="workExperienceInfo">
                  <li>Delivered full-stack projects end-to-end, from scoping and architecture through deployment and client handoff.</li>
                  <li>Adapted quickly across stacks — React, Node.js, WordPress, PHP — shipping production-ready work with minimal ramp-up.</li>
                  <li>Worked with 20+ clients across industries, delivering maintainable solutions with minimal handholding.</li>
                </ul>
              </li>
            </ul>
          </section>

          <section className="resumeEducation">
            <h2>Education</h2>
            <div className="educationWrapper">
              <h3>Indiana University, Bloomington</h3>
              <p>May 2022</p>
            </div>
            <div className="educationContent">
              <p>Luddy School of Informatics, Computing, and Engineering</p>
              <p>
                Bachelor of Science in Informatics
                <br />
                Human-Centered Computing Cognate, Web Design Minor
              </p>
            </div>
          </section>
        </div>
      </div>
    </main>
  );
};

export default transition(Resume);
